/**
 * Status — mirrors apps/experts/public/status.html (the public status page),
 * surfaced inside the admin shell. GET /api/status/page aggregates service
 * health, the last 30 production deploys, and incidents. The endpoint is public
 * (no admin gate on the API), but we still render it behind AdminGuard here so
 * the admin sub-nav entry is consistent; the page makes no privileged calls.
 */
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  StatusDot,
  Table,
  Td,
  Th,
  toneForStatus,
  Tr,
} from '../../components/primitives';
import { useStatusPage } from '../../data-access/useAdmin';
import { absTime, relTime } from '../../lib/format';

const OVERALL_LABEL: Record<string, string> = {
  healthy: 'All systems operational',
  degraded: 'Partial degradation',
  outage: 'Major outage',
};

export function StatusPage() {
  const { data, isPending, isError, error } = useStatusPage();

  return (
    <div>
      <AdminPageHeader
        subtitle={
          data
            ? `Generated ${relTime(data.generatedAt)}`
            : 'Service status, deploys, and incidents'
        }
        title="Status"
      />

      {isPending ? <AdminLoading /> : null}
      {isError ? (
        <AdminError
          message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
        />
      ) : null}

      {data ? (
        <div className="flex flex-col gap-4">
          <Panel>
            <div className="flex items-center gap-2 font-inter text-sm">
              <StatusDot tone={toneForStatus(data.overall)} />
              <span className="font-sans font-semibold text-content-primary">
                {OVERALL_LABEL[data.overall] ?? data.overall}
              </span>
            </div>
          </Panel>

          <Panel title="Services">
            <Table
              head={
                <>
                  <Th>Service</Th>
                  <Th>Status</Th>
                  <Th>Latency</Th>
                  <Th>Detail</Th>
                </>
              }
            >
              {data.services.map((svc) => (
                <Tr key={svc.id}>
                  <Td>{svc.label}</Td>
                  <Td>
                    <span className="inline-flex items-center gap-1.5">
                      <StatusDot tone={toneForStatus(svc.status)} />
                      {svc.status}
                    </span>
                  </Td>
                  <Td>
                    {svc.latency_ms != null ? `${svc.latency_ms}ms` : '—'}
                  </Td>
                  <Td className="text-content-secondary">
                    {svc.detail || '—'}
                  </Td>
                </Tr>
              ))}
            </Table>
          </Panel>

          <Panel title="Recent deploys">
            {data.deploys.length === 0 ? (
              <AdminEmpty>No recent deploys.</AdminEmpty>
            ) : (
              <Table
                head={
                  <>
                    <Th>SHA</Th>
                    <Th>Result</Th>
                    <Th>When</Th>
                  </>
                }
              >
                {data.deploys.slice(0, 15).map((d) => (
                  <Tr key={d.id}>
                    <Td>
                      {d.url ? (
                        <a
                          className="font-mono text-content-action text-xs hover:underline"
                          href={d.url}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {d.sha ?? d.id.slice(0, 7)}
                        </a>
                      ) : (
                        <Mono>{d.sha ?? '—'}</Mono>
                      )}
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={toneForStatus(d.conclusion)} />
                        {d.conclusion}
                      </span>
                    </Td>
                    <Td>{relTime(d.updatedAt ?? d.createdAt)}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>

          <Panel title="Incidents">
            {data.incidents.length === 0 ? (
              <AdminEmpty>No incidents recorded.</AdminEmpty>
            ) : (
              <ul className="flex flex-col gap-3">
                {data.incidents.map((inc) => (
                  <li className="font-inter text-sm" key={inc.id}>
                    <p className="flex items-center gap-2 font-medium text-content-primary">
                      <StatusDot
                        tone={inc.status === 'resolved' ? 'ok' : 'error'}
                      />
                      {inc.title}
                      <span className="text-content-tertiary text-xs">
                        ({inc.severity} · {inc.status})
                      </span>
                    </p>
                    <p className="text-content-secondary text-xs">
                      {absTime(inc.startedAt)}
                      {inc.resolvedAt ? ` → ${absTime(inc.resolvedAt)}` : ''}
                      {inc.affected.length
                        ? ` · affected: ${inc.affected.join(', ')}`
                        : ''}
                    </p>
                    {inc.updates.length ? (
                      <ul className="mt-1 list-inside list-disc text-content-secondary text-xs">
                        {inc.updates.map((u, i) => (
                          <li key={`${inc.id}-${i}`}>
                            {u.at ? `${absTime(u.at)}: ` : ''}
                            {u.message}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      ) : null}
    </div>
  );
}

export default StatusPage;
